import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ShopifySettings.css';
import { useTranslation } from 'react-i18next';
import { 
    FaShopify, 
    FaPercentage, 
    FaSave, 
    FaSpinner,
    FaExclamationTriangle,
    FaCheck,
    FaEdit,
    FaPalette,
    FaStore,
    FaCode,
    FaEye
} from 'react-icons/fa';

const ShopifySettings = () => {
    const [user, setUser] = useState(null);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);
    const [settingsData, setSettingsData] = useState(null);
    
    // Live form settings state
    const [liveFormSettings, setLiveFormSettings] = useState(null);
    const [isEditingLiveForm, setIsEditingLiveForm] = useState(false);
    const [isSavingLiveForm, setIsSavingLiveForm] = useState(false);
    const [liveFormError, setLiveFormError] = useState(null);
    const [liveFormSuccess, setLiveFormSuccess] = useState(null);
    const [liveFormData, setLiveFormData] = useState({
        colors: {
            primary_color: "#000000",
            secondary_color: "#FFFFFF",
            accent_color: "#007bff",
            background_color: "#f8f9fa",
            text_color: "#333333",
            button_color: "#007bff",
            button_text_color: "#FFFFFF"
        },
        branding: {
            company_logo: "",
            company_name: "",
            favicon: ""
        },
        custom_css: "",
        show_powered_by: true,
        form_title: "Live Shopping",
        form_description: ""
    });

    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                await Promise.all([
                    fetchUserData(token),
                    fetchDiscountPercentage(token),
                    fetchLiveFormSettings(token)
                ]);
            } catch (error) {
                console.error('Error loading Shopify settings:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            navigate('/');
        }
    };

    const fetchDiscountPercentage = async (token) => {
        try {
            const response = await axios.get('/api/shopify_settings/get_discount_percentage', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.status === 'success') {
                setDiscountPercentage(response.data.discount_percentage || 0);
                setSettingsData(response.data);
            } else {
                setDiscountPercentage(0);
                setSettingsData(null);
            }
        } catch (error) {
            console.error('Failed to fetch discount percentage:', error);
            setDiscountPercentage(0);
            setSettingsData(null);
        }
    };

    const fetchLiveFormSettings = async (token) => {
        try {
            const response = await axios.get('/api/shopify/live_form_settings', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setLiveFormSettings(response.data);
            setLiveFormData({
                colors: response.data.colors || liveFormData.colors,
                branding: response.data.branding || liveFormData.branding,
                custom_css: response.data.custom_css || "",
                show_powered_by: response.data.show_powered_by !== undefined ? response.data.show_powered_by : true,
                form_title: response.data.form_title || "Live Shopping",
                form_description: response.data.form_description || ""
            });
        } catch (error) {
            console.error('Failed to fetch live form settings:', error);
            setLiveFormSettings(null);
        }
    };

    const canManageShopifySettings = () => {
        return user && ['admin', 'seller', 'eshop'].includes(user.role);
    };

    // Shopify settings handlers
    const handleEdit = () => {
        if (!canManageShopifySettings()) return;
        setIsEditing(true);
        setFormError(null);
        setFormSuccess(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormError(null);
        setFormSuccess(null);
        setDiscountPercentage(settingsData?.discount_percentage || 0);
    };

    const handleInputChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setDiscountPercentage(value);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setFormError(null);
        setFormSuccess(null);

        const token = localStorage.getItem('access_token');

        if (discountPercentage < 0 || discountPercentage > 100) {
            setFormError(t('invalidDiscountPercentage', 'Discount percentage must be between 0 and 100'));
            setIsSaving(false);
            return;
        }

        try {
            const response = await axios.post('/api/shopify_settings/update_discount_percentage', {
                discount_percentage: discountPercentage
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('discountPercentageUpdated', 'Discount percentage updated successfully!'));
            setIsEditing(false);
            
            await fetchDiscountPercentage(token);
            
            setTimeout(() => {
                setFormSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Failed to update discount percentage:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToUpdateDiscountPercentage', 'Failed to update discount percentage. Please try again.')
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Live form settings handlers
    const handleEditLiveForm = () => {
        if (!canManageShopifySettings()) return;
        setIsEditingLiveForm(true);
        setLiveFormError(null);
        setLiveFormSuccess(null);
    };

    const handleCancelLiveForm = () => {
        setIsEditingLiveForm(false);
        setLiveFormError(null);
        setLiveFormSuccess(null);
        // Reset to original values
        if (liveFormSettings) {
            setLiveFormData({
                colors: liveFormSettings.colors || liveFormData.colors,
                branding: liveFormSettings.branding || liveFormData.branding,
                custom_css: liveFormSettings.custom_css || "",
                show_powered_by: liveFormSettings.show_powered_by !== undefined ? liveFormSettings.show_powered_by : true,
                form_title: liveFormSettings.form_title || "Live Shopping",
                form_description: liveFormSettings.form_description || ""
            });
        }
    };

    const handleLiveFormInputChange = (section, field, value) => {
        if (section === 'colors' || section === 'branding') {
            setLiveFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        } else {
            setLiveFormData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleSaveLiveForm = async (e) => {
        e.preventDefault();
        setIsSavingLiveForm(true);
        setLiveFormError(null);
        setLiveFormSuccess(null);

        const token = localStorage.getItem('access_token');

        try {
            const response = await axios.put('/api/shopify/live_form_settings', liveFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setLiveFormSuccess(t('liveFormSettingsUpdated', 'Live form settings updated successfully!'));
            setIsEditingLiveForm(false);
            
            await fetchLiveFormSettings(token);
            
            setTimeout(() => {
                setLiveFormSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Failed to update live form settings:', error);
            setLiveFormError(
                error.response?.data?.detail || 
                t('failedToUpdateLiveFormSettings', 'Failed to update live form settings. Please try again.')
            );
        } finally {
            setIsSavingLiveForm(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="una-shopify-settings-loading">
                <FaSpinner className="una-spinner" />
                <h2>{t('loadingShopifySettings', 'Loading Shopify settings...')}</h2>
            </div>
        );
    }

    if (!canManageShopifySettings()) {
        return (
            <div className="una-shopify-settings-unauthorized">
                <FaExclamationTriangle className="una-warning-icon" />
                <h2>{t('accessDenied', 'Access Denied')}</h2>
                <p>{t('noPermissionShopifySettings', 'You do not have permission to manage Shopify settings.')}</p>
            </div>
        );
    }

    return (
        <div className="una-shopify-settings-container">
            <div className="una-shopify-settings-header">
                <div className="una-shopify-settings-title">
                    <FaShopify className="una-shopify-icon" />
                    <h1>{t('shopifySettings', 'Shopify Settings')}</h1>
                </div>
            </div>

            <div className="una-shopify-settings-content">
                {/* Existing Shopify Discount Settings */}
                <div className="una-settings-card">
                    <div className="una-settings-card-header">
                        <div className="una-settings-title-section">
                            <h3>
                                <FaPercentage className="una-percentage-icon" />
                                {t('discountPercentage', 'Discount Percentage')}
                            </h3>
                            <p className="una-settings-description">
                                {t('discountPercentageDescription', 'Configure the default discount percentage for your Shopify store.')}
                            </p>
                        </div>
                        
                        {!isEditing && (
                            <button
                                className="una-edit-button"
                                onClick={handleEdit}
                                title={t('editDiscountPercentage', 'Edit Discount Percentage')}
                            >
                                <FaEdit />
                                {t('edit', 'Edit')}
                            </button>
                        )}
                    </div>
                    
                    <div className="una-settings-card-content">
                        {isEditing ? (
                            <form onSubmit={handleSave} className="una-discount-form">
                                <div className="una-form-group">
                                    <label htmlFor="discountPercentage">
                                        {t('discountPercentage', 'Discount Percentage')} (0-100) *
                                    </label>
                                    <div className="una-percentage-input-group">
                                        <input
                                            type="number"
                                            id="discountPercentage"
                                            value={discountPercentage}
                                            onChange={handleInputChange}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            required
                                            className="una-percentage-input"
                                            placeholder="0.0"
                                        />
                                        <span className="una-percentage-symbol">%</span>
                                    </div>
                                </div>

                                {formError && <p className="una-error-message">{formError}</p>}
                                {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                                <div className="una-form-actions">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="una-button una-button-secondary"
                                    >
                                        {t('cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="una-button una-button-primary"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? (
                                            <>
                                                <FaSpinner className="una-spinner" />
                                                {t('saving', 'Saving...')}
                                            </>
                                        ) : (
                                            <>
                                                <FaSave />
                                                {t('save', 'Save')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="una-discount-display">
                                <div className="una-current-value">
                                    <span className="una-value-label">{t('currentValue', 'Current Value')}: </span>
                                    <span className="una-value-display">
                                        {discountPercentage}%
                                    </span>
                                </div>
                                
                                {settingsData && (
                                    <div className="una-settings-meta">
                                        {settingsData.created_at && (
                                            <span className="una-meta-item">
                                                {t('created', 'Created')}: {formatDate(settingsData.created_at)}
                                            </span>
                                        )}
                                        {settingsData.updated_at && (
                                            <span className="una-meta-item">
                                                {t('lastUpdated', 'Last Updated')}: {formatDate(settingsData.updated_at)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* New Live Form Settings */}
                <div className="una-settings-card">
                    <div className="una-settings-card-header">
                        <div className="una-settings-title-section">
                            <h3>
                                <FaStore className="una-store-icon" />
                                {t('liveFormSettings', 'Live Form Settings')}
                            </h3>
                            <p className="una-settings-description">
                                {t('liveFormSettingsDescription', 'Customize the appearance and branding of your live shopping forms.')}
                            </p>
                        </div>
                        
                        {!isEditingLiveForm && (
                            <button
                                className="una-edit-button"
                                onClick={handleEditLiveForm}
                                title={t('editLiveFormSettings', 'Edit Live Form Settings')}
                            >
                                <FaEdit />
                                {t('edit', 'Edit')}
                            </button>
                        )}
                    </div>
                    
                    <div className="una-settings-card-content">
                        {isEditingLiveForm ? (
                            <form onSubmit={handleSaveLiveForm} className="una-live-form-settings">
                                {/* Form Title and Description */}
                                <div className="una-form-section">
                                    <h4><FaEye /> {t('formContent', 'Form Content')}</h4>
                                    <div className="una-form-group">
                                        <label htmlFor="formTitle">
                                            {t('formTitle', 'Form Title')}
                                        </label>
                                        <input
                                            type="text"
                                            id="formTitle"
                                            value={liveFormData.form_title}
                                            onChange={(e) => handleLiveFormInputChange(null, 'form_title', e.target.value)}
                                            className="una-text-input"
                                            placeholder="Live Shopping"
                                        />
                                    </div>
                                    <div className="una-form-group">
                                        <label htmlFor="formDescription">
                                            {t('formDescription', 'Form Description')}
                                        </label>
                                        <textarea
                                            id="formDescription"
                                            value={liveFormData.form_description}
                                            onChange={(e) => handleLiveFormInputChange(null, 'form_description', e.target.value)}
                                            className="una-textarea-input"
                                            rows="3"
                                            placeholder="Enter form description..."
                                        />
                                    </div>
                                </div>

                                {/* Colors Section */}
                                <div className="una-form-section">
                                    <h4><FaPalette /> {t('colors', 'Colors')}</h4>
                                    <div className="una-color-grid">
                                        <div className="una-form-group">
                                            <label htmlFor="primaryColor">
                                                {t('primaryColor', 'Primary Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="primaryColor"
                                                value={liveFormData.colors.primary_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'primary_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                        <div className="una-form-group">
                                            <label htmlFor="secondaryColor">
                                                {t('secondaryColor', 'Secondary Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="secondaryColor"
                                                value={liveFormData.colors.secondary_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'secondary_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                        <div className="una-form-group">
                                            <label htmlFor="accentColor">
                                                {t('accentColor', 'Accent Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="accentColor"
                                                value={liveFormData.colors.accent_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'accent_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                        <div className="una-form-group">
                                            <label htmlFor="backgroundColor">
                                                {t('backgroundColor', 'Background Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="backgroundColor"
                                                value={liveFormData.colors.background_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'background_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                        <div className="una-form-group">
                                            <label htmlFor="textColor">
                                                {t('textColor', 'Text Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="textColor"
                                                value={liveFormData.colors.text_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'text_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                        <div className="una-form-group">
                                            <label htmlFor="buttonColor">
                                                {t('buttonColor', 'Button Color')}
                                            </label>
                                            <input
                                                type="color"
                                                id="buttonColor"
                                                value={liveFormData.colors.button_color}
                                                onChange={(e) => handleLiveFormInputChange('colors', 'button_color', e.target.value)}
                                                className="una-color-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Branding Section */}
                                <div className="una-form-section">
                                    <h4><FaShopify /> {t('branding', 'Branding')}</h4>
                                    <div className="una-form-group">
                                        <label htmlFor="companyName">
                                            {t('companyName', 'Company Name')}
                                        </label>
                                        <input
                                            type="text"
                                            id="companyName"
                                            value={liveFormData.branding.company_name}
                                            onChange={(e) => handleLiveFormInputChange('branding', 'company_name', e.target.value)}
                                            className="una-text-input"
                                            placeholder="Your Company Name"
                                        />
                                    </div>
                                    <div className="una-form-group">
                                        <label htmlFor="companyLogo">
                                            {t('companyLogo', 'Company Logo URL')}
                                        </label>
                                        <input
                                            type="url"
                                            id="companyLogo"
                                            value={liveFormData.branding.company_logo}
                                            onChange={(e) => handleLiveFormInputChange('branding', 'company_logo', e.target.value)}
                                            className="una-text-input"
                                            placeholder="https://example.com/logo.png"
                                        />
                                    </div>
                                    <div className="una-form-group">
                                        <label htmlFor="favicon">
                                            {t('favicon', 'Favicon URL')}
                                        </label>
                                        <input
                                            type="url"
                                            id="favicon"
                                            value={liveFormData.branding.favicon}
                                            onChange={(e) => handleLiveFormInputChange('branding', 'favicon', e.target.value)}
                                            className="una-text-input"
                                            placeholder="https://example.com/favicon.ico"
                                        />
                                    </div>
                                </div>

                                {/* Advanced Settings */}
                                <div className="una-form-section">
                                    <h4><FaCode /> {t('advanced', 'Advanced')}</h4>
                                    <div className="una-form-group">
                                        <label htmlFor="customCss">
                                            {t('customCss', 'Custom CSS')}
                                        </label>
                                        <textarea
                                            id="customCss"
                                            value={liveFormData.custom_css}
                                            onChange={(e) => handleLiveFormInputChange(null, 'custom_css', e.target.value)}
                                            className="una-textarea-input una-code-input"
                                            rows="6"
                                            placeholder="/* Add your custom CSS here */"
                                        />
                                    </div>
                                    <div className="una-form-group">
                                        <label className="una-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={liveFormData.show_powered_by}
                                                onChange={(e) => handleLiveFormInputChange(null, 'show_powered_by', e.target.checked)}
                                                className="una-checkbox-input"
                                            />
                                            {t('showPoweredBy', 'Show "Powered by" branding')}
                                        </label>
                                    </div>
                                </div>

                                {liveFormError && <p className="una-error-message">{liveFormError}</p>}
                                {liveFormSuccess && <p className="una-success-message">{liveFormSuccess}</p>}

                                <div className="una-form-actions">
                                    <button
                                        type="button"
                                        onClick={handleCancelLiveForm}
                                        className="una-button una-button-secondary"
                                    >
                                        {t('cancel', 'Cancel')}
                                    </button>
                                    <button
                                        type="submit"
                                        className="una-button una-button-primary"
                                        disabled={isSavingLiveForm}
                                    >
                                        {isSavingLiveForm ? (
                                            <>
                                                <FaSpinner className="una-spinner" />
                                                {t('saving', 'Saving...')}
                                            </>
                                        ) : (
                                            <>
                                                <FaSave />
                                                {t('save', 'Save')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="una-live-form-display">
                                <div className="una-settings-summary">
                                    <div className="una-summary-item">
                                        <span className="una-summary-label">{t('formTitle', 'Form Title')}: </span>
                                        <span className="una-summary-value">{liveFormData.form_title || 'Live Shopping'}</span>
                                    </div>
                                    <div className="una-summary-item">
                                        <span className="una-summary-label">{t('companyName', 'Company Name')}: </span>
                                        <span className="una-summary-value">{liveFormData.branding.company_name || t('notSet', 'Not set')}</span>
                                    </div>
                                    <div className="una-summary-item">
                                        <span className="una-summary-label">{t('primaryColor', 'Primary Color')}: </span>
                                        <span className="una-color-preview" style={{ backgroundColor: liveFormData.colors.primary_color }}></span>
                                        <span className="una-summary-value">{liveFormData.colors.primary_color}</span>
                                    </div>
                                </div>
                                
                                {liveFormSettings && (
                                    <div className="una-settings-meta">
                                        {liveFormSettings.created_at && (
                                            <span className="una-meta-item">
                                                {t('created', 'Created')}: {formatDate(liveFormSettings.created_at)}
                                            </span>
                                        )}
                                        {liveFormSettings.updated_at && (
                                            <span className="una-meta-item">
                                                {t('lastUpdated', 'Last Updated')}: {formatDate(liveFormSettings.updated_at)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(formSuccess || liveFormSuccess) && !isEditing && !isEditingLiveForm && (
                <div className="una-success-banner">
                    <FaCheck className="una-success-icon" />
                    <span>{formSuccess || liveFormSuccess}</span>
                </div>
            )}
        </div>
    );
};

export default ShopifySettings;